import { Button, Notification, toast } from 'components/ui';
import { CompanyContext } from 'contexts/CompanyContext';
import { ScheduledPostContext } from 'contexts/ScheduledPostContext';
import { UploadContext } from 'contexts/UploadContext';
import { t } from 'i18next';
import { capitalize } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { api as Api } from 'api/http';
import { formatSocialPostObj } from 'utils/formatSocialPostObj'
import getPreviewComponent from 'utils/getPreviewComponent';
import usePostValidator from 'utils/hooks/usePostValidator';
import PostArticleButton from './components/PostArticleButton';
import PostArticleSocials from './components/PostArticleSocials';
import PostArticleOptionsButton from './components/PostArticleOptionsButton';
import useBus from 'use-bus';
import { CalendarActiveSocialNetworkContext } from 'contexts/CalendarActiveSocialNetworkContext';
import { downloadAsFile } from 'utils/downloadAsFile';
import { slugify } from 'utils/slugify';
import { HiOutlineChevronDown, HiOutlineChevronUp } from 'react-icons/hi2';
import { getCalendarDurationPerAspectRatio } from 'utils/getAspectRatio';
import { Draggable } from '@fullcalendar/interaction';
import { SessionContext } from 'contexts/SessionContext';
import ValidationErrors from './components/ValidationErrors';
import PublishErrors from './components/PublishErrors';   
import PostTagsButton from './components/PostTagsButton';
import { getVideoCoverImageBlob } from 'utils/getVideoCoverImageBlob';
import { MediaContext } from 'contexts/MediaContext';
import dayjs from 'dayjs';
import usePostGenerator from 'utils/hooks/usePostGenerator';
import { allowedCommentsNetworks } from "utils/comments";
import { HiOutlineX } from 'react-icons/hi';
import { getNetworkValidationIndex } from 'utils/hooks/usePostValidator';
import { notifyPostGroupSaved } from 'utils/postSaveNotifier';
import { toastPublishNowResults } from './utils/usePostGroupActions';
import { POST_COMPOSER_ENABLED } from 'components/ui/utils/constant';
import PostComposer from './components/composer/PostComposer';

const PostArticle = ({ 
    item,
    forceFullScreen = false,
    setOpenedPostGroup = (val) => void 0,
    previewZoom = 0.9,
    expandablePost = false,
    forceNoEdit = false,
    showOptionsButton = true,
    showPostArticleButton = true,
    btnSize = "sm", 
    isDraggable = false,
    className = "",
    style = undefined,
    setPostNeedsSaving = undefined,
    uniqueId = undefined,
    duplicate = undefined,
    onRemovePost = undefined,
    updatePost = undefined,
    isSimpleView = false
}) => {
    const { updateMyMedia } = useContext(MediaContext);
    const { urlToFileUploadModal, sendFileToAPIUploadModal, blobToFile, sendFileToAnyAPIEndpoint } = useContext(UploadContext);
    const { getScheduledPostGroup, triggerUpdate, setScheduledPostGroupFieldValue } = useContext(ScheduledPostContext);
    const { company, companySocials } = useContext(CompanyContext);
    const { calendarActiveSocialNetwork } = useContext(CalendarActiveSocialNetworkContext);
    const { updateBalance } = useContext(SessionContext);
    const { getErrors, validationConstants, getFirstError, getDescriptionLengthErrors } = usePostValidator();
    const { generateEmptyPost } = usePostGenerator();
    
    const [ showSaveButton, setShowSaveButton ] = useState(!item?.id);
    const [ isLoadingPostArticleButton, setIsLoadingPostArticleButton ] = useState(false);
    const [ postGroup, setPostGroup ] = useState(item);
    const [ activeSocialNetwork, setActiveSocialNetwork ] = useState(postGroup?.posts?.[0]?.social_network_type);
    const [ validationErrors, setValidationErrors ] = useState();
    const [ fullScreen, setFullScreen ] = useState(forceFullScreen);
    const [ postExpanded, setPostExpanded ] = useState(!expandablePost);

    const getFormatedPosts = (_posts) => {
        return _posts?.map((post, index) => {
            return formatSocialPostObj(
                post?.social_network_type,
                post?.id,
                post?.user_id,
                post?.user_name,
                post?.username,
                post?.user_image,
                postGroup?.created_at,
                post?.description,
                [],
                undefined,
                undefined,
                undefined,
                undefined,
                post?.title,
                post?.link,
                undefined,
                post?.preferences?.media_product_type,
                undefined,
                undefined,
                post?.assets,
                post?.preferences,
                index,
                0,
                post?.comments_to_create,
                post?.cover
            )
        });
    }

    const postRef = useRef();

    const posts = getFormatedPosts(postGroup?.posts);
    
    expandablePost = fullScreen ? false : expandablePost;

    previewZoom = fullScreen ? 0.9 : previewZoom;

    const published = postGroup?.posts?.filter(p => p.status != "published")?.length == 0;
    const publishing = postGroup?.posts?.filter(p => p.status == "publishing")?.length > 0 /*|| (postGroup?.posts?.filter(p => p.status == "published")?.length > 0 && postGroup?.posts?.filter(p => p.status == "pending")?.length > 0);*/
    const error = postGroup?.posts?.filter(p => p.status == "error" && !!p.status_message)?.length > 0; 
    const status = published ? "published" : publishing ? "publishing" : error ? "error" : "pending";

    const isEditable = (status == "draft" || status == "pending" || status == "error" || !postGroup?.id) && (!forceNoEdit || fullScreen);
    const postIndex = posts?.findIndex(e => e.network == activeSocialNetwork);

    const PreviewComponent = getPreviewComponent(posts?.filter(e => e.network == activeSocialNetwork)?.[0]);

    const updateFieldValuePostGroup = async (field, value, isRefreshFromDb = true) => {
        if (!postGroup?.id) return;

        const isScheduling = field === 'publish_at' && value != null;
        const isUndrafting = field === 'draft' && !value;
        if (isScheduling || isUndrafting) {
            const error = await getFirstError(posts);
            if (error) {
                toast.push(<Notification title={error} type="danger" />);
                return;
            }
        }

        setIsLoadingPostArticleButton(true);
        var obj = {};
        obj[field] = value;
        setScheduledPostGroupFieldValue(postGroup?.id, field, value);
        await Api.put(`/postGroups/${postGroup?.id}`, {}, obj).then(() => {
            toast.push(<Notification title="Post updated successfully!" type="success" />);
            isRefreshFromDb && refreshFromDb();
        }).catch((err) => {
            toast.push(<Notification title={err?.message} type="danger" />);
        }).finally(() => {
            setIsLoadingPostArticleButton(false);
        });
    }

    const refreshFromDb = async (_postGroupID = undefined) => {
        _postGroupID = _postGroupID || postGroup?.id;
        if (!_postGroupID) return;
        const response = await getScheduledPostGroup(_postGroupID);
        setPostGroup({
            ...response,
            randomId: postGroup?.randomId
        });
        updatePost && updatePost(response);
        setShowSaveButton(false);
        triggerUpdate();
    }

    const onAssetDelete = (postIndex = 0, assetIndex = 0) => {
        let _newPostGroup = { ...postGroup };
        _newPostGroup?.posts[postIndex]?.assets?.splice(assetIndex, 1);
        setPostGroup(_newPostGroup);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
    }

    const onAssetEdit = (postIndex = 0, assetIndex = 0, src = "") => {
        let _newPostGroup = { ...postGroup };
        _newPostGroup.posts[postIndex].assets[assetIndex].image.url = src;
        _newPostGroup.posts[postIndex].assets[assetIndex].image.thumbnail = { url: src };
        _newPostGroup.posts[postIndex].assets[assetIndex].id = undefined;
        setPostGroup(_newPostGroup);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
    }

    const onAssetNew = (postIndex = 0, asset) => {
        let _newPostGroup = { ...postGroup };
        _newPostGroup.posts[postIndex].assets = [...(_newPostGroup.posts[postIndex].assets || []), asset];
        setPostGroup(_newPostGroup);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
    }

    const onTextEdit = (postIndex = 0, text = "") => {
        let _newPostGroup = { ...postGroup };
        _newPostGroup.posts[postIndex].description = text;
        setPostGroup(_newPostGroup);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
    }

    const onCommentsToCreateEdit = (postIndex = 0, comments = []) => {
        let _newPostGroup = { ...postGroup };
        _newPostGroup.posts[postIndex].comments_to_create = comments;
        setPostGroup(_newPostGroup);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
    }

    const onTitleEdit = (postIndex = 0, title = "") => {
        let _newPostGroup = { ...postGroup };
        _newPostGroup.posts[postIndex].title = title;
        setPostGroup(_newPostGroup);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
    }

    const onLinkEdit = (postIndex = 0, link = "") => {
        let _newPostGroup = { ...postGroup };
        _newPostGroup.posts[postIndex].link = link;
        setPostGroup(_newPostGroup);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
    }

    const onPreferencesEdit = (postIndex = 0, preferences = {}) => {
        let _newPostGroup = { ...postGroup };

        _newPostGroup.posts[postIndex].preferences = preferences;
        setPostGroup(_newPostGroup);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
    }

    const onPostNow = async () => {
        setIsLoadingPostArticleButton(true);
        const error = await getFirstError(posts);

        if (error) {
            toast.push(<Notification title={error} type="danger" />);
        }else {
            try {
                const unpublishedPosts = postGroup?.posts?.filter(p => p?.status == "pending" || p?.status == "error");
                const uniqueNetworkTypes = [...new Set(unpublishedPosts.map(post => post.social_network_type))];
                const results = await Promise.allSettled(
                    uniqueNetworkTypes.map(networkType =>
                        Api.post(`/postGroups/${postGroup.id}/publish`, {}, {
                            social_network_type: networkType,
                            update_publish_at: true
                        })
                    )
                );
                toastPublishNowResults(results);
                await refreshFromDb();
                // Notify generation system that post was published
                notifyPostGroupSaved(postGroup?.id, { 
                    postCount: postGroup?.posts?.length,
                    template: postGroup?.template,
                    action: 'published'
                });
            } catch (err) {
                toast.push(<Notification title={err?.message || err} type="danger" />);
            }
        }
        
        setIsLoadingPostArticleButton(false);
    }

    const onSave = async () => {
        if (isLoadingPostArticleButton) return;
        // Saving queues the group (draft 0, auto_publish 1) and the queue publishes it later
        // without checking again, so a Threads part over the limit must be fixed now. Checked
        // before any request, so a blocked save leaves nothing half saved.
        const threadsLengthError = getDescriptionLengthErrors(posts?.filter(p => p?.network === "threads"))?.[0];
        if (threadsLengthError) {
            toast.push(<Notification title={threadsLengthError.error} type="danger" />);
            return;
        }
        setIsLoadingPostArticleButton(true);
        try {
            // Post Group
            let _postGroupData = { 
                ...postGroup, 
                tags_ids: postGroup?.tags?.map(e => e.id) || undefined, 
                draft: 0, 
                auto_publish: 1 
            };
            
            _postGroupData.posts = undefined;
            const _postGroupID = _postGroupData?.id 
                ? await Api.put(`/postGroups/${_postGroupData?.id}`, {}, _postGroupData).then(({ data: _postGroup }) => { return _postGroup?.id; }) 
                : await Api.post(`/companies/${company?.id}/postGroups`, {}, _postGroupData).then(({ data: _postGroup }) => { return _postGroup?.id; });
            // Assets
            let _posts = posts;

            for (var i = 0; i < _posts.length; i++) {
                // General assets
                for (var j = 0; j < _posts[i]?.assets?.length; j++) {
                    let _asset = _posts[i]?.assets[j];
                    if (!_asset?.id) {
                        const file = await urlToFileUploadModal(_asset?.image?.url || _asset?.video?.url);
                        _asset = await sendFileToAPIUploadModal(file, file?.name, undefined, undefined);
                        _posts[i].assets[j].id = _asset?.id;
                    }
                }
            }
            // Format Posts
            _posts = _posts.map((_post) => {
                const post = {
                    id: _post?.id,
                    social_network_type: _post?.social_network_type || _post?.network,
                    title: _post?.title,
                    description: _post?.preferences?.media_product_type == "STORY" ? "" : _post?.text?.trim(),
                    link: _post?.link,
                    assets_ids: !!_post?.assets?.length ? _post?.assets?.map(e => e?.id) : [],
                    cover: _post?.cover || undefined,
                    preferences: {
                        notify_followers: 1, //_post?.preferences?.notify_followers ? 1 : 0,
                        media_product_type: _post?.preferences?.media_product_type || undefined,
                        board_id: _post?.preferences?.board_id || undefined,
                        privacy_level: _post?.preferences?.privacy_level || undefined,
                        disable_comment: _post?.preferences?.disable_comment || undefined,
                        disable_duet: _post?.preferences?.disable_duet || undefined,
                        disable_stitch: _post?.preferences?.disable_stitch || undefined,
                        video_cover_timestamp_ms: _post?.preferences?.video_cover_timestamp_ms || undefined,
                        playlist_id: _post?.preferences?.playlist_id || undefined,
                        category_id: _post?.preferences?.category_id || undefined,
                        tags: _post?.preferences?.tags || undefined,
                    }
                }

                if (allowedCommentsNetworks.includes(post.social_network_type)) {
                    post.comments_to_create = _post.comments_to_create || [];
                }

                return post;
            });
            // Delete Posts
            const _dbPostGroup = await getScheduledPostGroup(_postGroupID);
            const _deletedPosts = _dbPostGroup?.posts?.filter(e => !_posts?.map(_p => _p?.id)?.filter(e => !!e)?.includes(e?.id));
            if (_deletedPosts?.length) {
                const deleteResponses = await Promise.allSettled(_deletedPosts.map((_deletedPost) => Api.delete(`/posts/${_deletedPost?.id}`)));
            }
            // Create/Update Posts
            let errors = [];
            for (var i = 0; i < _posts?.length; i++) {
                const _post = _posts[i];
                if (_post?.id) {
                    await Api.put(`/posts/${_post?.id}`, {}, _post).catch((err) => errors.push({ i, err: err })); 
                } else {
                    const response = await Api.post(`/postGroups/${_postGroupID}/posts`, {}, _post).catch((err) => errors.push({ i, err: err }));
                    const _db_post_id = response?.data?.id;
                    _posts[i].id = _db_post_id;
                }
            }

            // Video thumbnail image / Video cover image
            for (var i = 0; i < _posts.length; i++) {
                _posts[i].assets = posts[i]?.assets;
                
                const networkValidationIndex = getNetworkValidationIndex(_posts[i]?.social_network_type, _posts[i]?.preferences?.media_product_type);
                const constants = validationConstants?.[networkValidationIndex];

                if (
                    constants?.media_urls?.required_cover && 
                    !_posts[i]?.preferences?.video_cover_timestamp_ms && 
                    !_posts[i]?.cover?.url
                ) {
                    _posts[i].preferences.video_cover_timestamp_ms = 0;
                }

                if (
                    _posts[i]?.assets?.filter(_asset => !!_asset?.video?.url)?.length > 0 && 
                    (constants?.media_urls?.allow_file_cover || constants?.media_urls?.allow_frame_cover) &&
                    (_posts[i]?.preferences?.video_cover_timestamp_ms !== undefined || _posts[i]?.cover?.url)
                ) {
                    if (_posts[i]?.cover?.url) {
                        await Api.post(`/posts/${_posts[i]?.id}/cover`, {}, {
                            url: _posts[i]?.cover?.url,
                            visibility: "public",
                            filename: _posts[i]?.cover?.url.split('/').pop().split('?')[0]
                        });
                    } else if (constants?.media_urls?.allow_file_cover && _posts[i]?.preferences?.video_cover_timestamp_ms) {
                        const videoURL = _posts[i]?.assets?.find(_asset => !!_asset?.video?.url)?.video?.url;
                        const videoFile = await urlToFileUploadModal(videoURL); 
                        const blobCover = await getVideoCoverImageBlob(
                        videoFile, 
                        _posts[i]?.preferences?.video_cover_timestamp_ms 
                            ? _posts[i]?.preferences?.video_cover_timestamp_ms / 1000 
                            : undefined
                        );
                        const coverFile = blobToFile(
                            blobCover, 
                            `${_posts[i]?.social_network_type}_cover_${_posts[i]?.id}_${new Date().getTime()}.jpeg`
                        );
                        await sendFileToAnyAPIEndpoint(
                            `/posts/${_posts[i]?.id}/cover`, 
                            coverFile
                        );
                    }
                }
            }
            if (!errors?.length) {
                toast.push(<Notification title="Post saved!" type="success" />);
                // Notify generation system that post was saved
                notifyPostGroupSaved(_postGroupID, { 
                    postCount: _posts.length,
                    template: postGroup?.template 
                });
                // Refresh from DB
                await refreshFromDb(_postGroupID);
                updateMyMedia();
                updateBalance();
            } else {
                errors.map(({ i, err }) => {
                    if (err) {
                        toast.push(<Notification title={`${capitalize(_posts[i]?.network)}: ${err}`} type="danger" />);
                    }
                });
            }

        } catch (err) {
            toast.push(<Notification title={err?.message || err} type="danger" />);
        }
        triggerUpdate();
        setIsLoadingPostArticleButton(false);
    }

    const onCreateNetworkPost = (_post, deletePreviousNetworkPosts = false) => {
        let _newPostGroup = deletePreviousNetworkPosts
            ? onDeleteNetworkPost(_post?.social_network_type)
            : { ...postGroup };
        _newPostGroup.posts.push(_post);
        setPostGroup(_newPostGroup);
        setActiveSocialNetwork(_post?.social_network_type);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
        return _newPostGroup;
    }

    const onDeleteIndexPost = (postIndex) => {
        let _newPostGroup = { ...postGroup };
        _newPostGroup.posts = _newPostGroup.posts.filter((e, i) => i != postIndex);
        setPostGroup(_newPostGroup);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
        return _newPostGroup;
    }

    const onDeleteNetworkPost = (network) => {
        let _newPostGroup = { ...postGroup };
        _newPostGroup.posts = _newPostGroup.posts.filter(e => e?.social_network_type != network);
        setPostGroup(_newPostGroup);
        setActiveSocialNetwork(_newPostGroup.posts?.[0]?.social_network_type);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
        return _newPostGroup;
    }

    const onReplaceNetworkPost = (network, _post) => {
        let _newPostGroup = { ...postGroup };
        _newPostGroup.posts = _newPostGroup.posts.filter(e => e?.social_network_type != network);
        _newPostGroup.posts.push(_post);
        setPostGroup(_newPostGroup);
        setActiveSocialNetwork(_post?.social_network_type);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
        return _newPostGroup;
    }

    const onDeletePostGroup = async () => {
        if (postGroup?.id) {
            Api.delete(`/postGroups/${postGroup?.id}`).then((response) => {
                toast.push(<Notification title={t(`postModal.posthavebeen`)} type="success" />);
                triggerUpdate();
            }).catch((err) => {
                toast.push(<Notification title={err?.message} type="danger" />);
            });
        }
        onRemovePost && onRemovePost(postGroup);
    }

    const onToggleFullScreen = () => {
        setOpenedPostGroup(undefined);
        setFullScreen(!fullScreen);
    }

    const onExportAsHTML = () => {
        downloadAsFile(posts?.[postIndex]?.text, `${slugify(`${posts?.[postIndex]?.title} ${new Date().toString()}`)}.html`);
    }

    const onClickTag = (tag) => {
        let _postGroup = { ...postGroup };
        if (postGroup?.tags?.find(e => e?.id == tag.id)) {
            _postGroup.tags = _postGroup.tags.filter(e => e.id != tag.id);
            setPostGroup(_postGroup);
            if (postGroup?.id) {
                Api.put(`/postGroups/${postGroup?.id}`, { tags_ids: [tag.id], __mode: 'remove' }).then(() => {
                    triggerUpdate();
                });
            }
        } else {
            _postGroup['tags'] = _postGroup?.tags || []
            _postGroup.tags.push(tag);
            setPostGroup(_postGroup); 
            if (postGroup?.id) {
                Api.put(`/postGroups/${postGroup?.id}`, { tags_ids: [tag.id], __mode: 'append' }).then(() => {
                    triggerUpdate();
                });
            }
        }
    }

    const onDuplicatePostGroup = () => {
        const duplicatedPostGroup = generateEmptyPost(postGroup.posts[0].social_network_type);

        // Copy relevant data from the original postGroup
        duplicatedPostGroup.id = postGroup?.id;
        duplicatedPostGroup.randomId = postGroup?.randomId;
        duplicatedPostGroup.tags_ids = postGroup?.tags_ids;
        duplicatedPostGroup.tags = postGroup?.tags;
        duplicatedPostGroup.posts = postGroup.posts.map(post => {
            return {
                id: undefined,
                user_id: post?.user_id,
                social_network_type: post.social_network_type,
                description: post.description,
                assets: post.assets.map(asset => ({ 
                    ...asset, 
                    id: undefined 
                })),
                title: post.title,
                link: post.link,
                preferences: post.preferences,
                comments_to_create: post.comments_to_create
            };
        }).filter(Boolean);

        if (duplicate) {
            duplicate(duplicatedPostGroup);
        }
        toast.push(<Notification title="Post duplicated!" type="success" />);
    }

    const onCoverEdit = (postIndex = 0, cover) => {
        let _newPostGroup = { ...postGroup };
        _newPostGroup.posts[postIndex].cover = cover;
        setPostGroup(_newPostGroup);
        setShowSaveButton(true);
        getValidations(_newPostGroup.posts);
        updatePost && updatePost(_newPostGroup);
    }

    const getValidations = (_posts = postGroup?.posts) => {
        _posts = getFormatedPosts(_posts);
        if (status != "published" && status != "publishing") {
            getErrors(_posts).then((errors) => {
                setValidationErrors(errors);
            });
        } else {
            setValidationErrors([]);
        }
    }

    useBus('saveAll', () => {
        if (showSaveButton || !postGroup?.id) {
            onSave();
        }
    }, [posts, showSaveButton, postGroup?.id]);

    useEffect(() => {
        if (showSaveButton || !postGroup?.id) {
            setPostNeedsSaving && setPostNeedsSaving(uniqueId);
        }
    }, [showSaveButton, uniqueId, postGroup?.id]);

    useEffect(() => {
        if (!postGroup?.posts?.[0]?.social_network_type) {
            let _newPosts = [...(postGroup?.posts || [])];
            if (!_newPosts?.length) _newPosts = [{}];
            _newPosts[0].social_network_type = calendarActiveSocialNetwork?.[0] || companySocials?.[0]?.type || "instagram";
            let _newPostGroup = { ...postGroup };
            _newPostGroup.posts = _newPosts;
            setPostGroup(_newPostGroup);
            setActiveSocialNetwork(_newPostGroup?.posts?.[0]?.social_network_type);
        }
    }, [postGroup, calendarActiveSocialNetwork]);

    useEffect(() => {
        getValidations();
    }, [posts?.length, status, validationConstants]);

    useEffect(() => {
        let interval = setInterval(
            status == "publishing" && 
            dayjs(postGroup?.publish_at).add(30, 'minutes').toDate() > new Date()
                ? refreshFromDb 
                : void 0
        , 10000);
        return () => clearInterval(interval);
    }, [status]);

    useEffect(() => {
        if ((!activeSocialNetwork || !PreviewComponent) && posts?.[0]?.network) {
            setActiveSocialNetwork(posts?.[0]?.network);
        }
    }, [activeSocialNetwork, PreviewComponent]);

    useEffect(() => {
        if (postRef.current && isDraggable && postGroup) {
            new Draggable(
                postRef.current, {
                    eventData: {
                        title: 'New event',
                        duration: getCalendarDurationPerAspectRatio(""),
                        post_group: postGroup
                    }
                }
            );
        }
    }, [postRef, postGroup]);

    if (!postGroup?.posts?.length) return <></>;
    if (!PreviewComponent) return <></>;

    // Flag-gated two-pane composer for fullscreen/modal editing. Covers both
    // entries (PostArticleContext's forceFullScreen modal and the grid card's
    // expand-to-fullscreen). Inline cards always keep the classic render below.
    if (fullScreen && POST_COMPOSER_ENABLED) {
        return (
            <PostComposer
                postGroup={postGroup}
                posts={posts}
                status={status}
                isEditable={isEditable}
                activeSocialNetwork={activeSocialNetwork}
                setActiveSocialNetwork={setActiveSocialNetwork}
                validationErrors={validationErrors}
                showSaveButton={showSaveButton}
                isLoadingPostArticleButton={isLoadingPostArticleButton}
                PreviewComponent={PreviewComponent}
                postIndex={postIndex}
                forceFullScreen={forceFullScreen}
                showOptionsButton={showOptionsButton}
                showPostArticleButton={showPostArticleButton}
                canDuplicate={!!duplicate}
                onTextEdit={onTextEdit}
                onTitleEdit={onTitleEdit}
                onLinkEdit={onLinkEdit}
                onAssetNew={onAssetNew}
                onAssetDelete={onAssetDelete}
                onAssetEdit={onAssetEdit}
                onCoverEdit={onCoverEdit}
                onPreferencesEdit={onPreferencesEdit}
                onCommentsToCreateEdit={onCommentsToCreateEdit}
                onCreateNetworkPost={onCreateNetworkPost}
                onDeleteNetworkPost={onDeleteNetworkPost}
                onReplaceNetworkPost={onReplaceNetworkPost}
                onDeleteIndexPost={onDeleteIndexPost}
                onSave={onSave}
                onPostNow={onPostNow}
                onDeletePostGroup={onDeletePostGroup}
                onDuplicatePostGroup={onDuplicatePostGroup}
                onExportAsHTML={onExportAsHTML}
                onClickTag={onClickTag}
                updateFieldValuePostGroup={updateFieldValuePostGroup}
                onClose={onToggleFullScreen}
            />
        );
    }

    return (
        <>
            {fullScreen && 
                <div className={`absolute inset-0 backdrop-blur bg-black/95`}/>
            }
            <div key={postGroup} ref={postRef} onClick={() => !!fullScreen && !forceFullScreen && onToggleFullScreen()} className={`transition-all dark:border-gray-700 w-full ${fullScreen && `fullScreen !fixed !left-0 !top-0 !w-full !h-full !p-4 !lg:p-6 !z-[10000] !overflow-y-auto flex`} ${className}`} style={style}>
                <div onClick={(e) => e.stopPropagation()} className={`rounded-2xl transition-all ${isDraggable && !fullScreen && `cursor-pointer hover:drop-shadow-[0_10px_8px_rgba(0,0,0,0.1)] hover:rotate-[2deg]`} ${fullScreen && `${activeSocialNetwork == "medium" ? `!max-w-[750px]` : `!max-w-[460px]`} !mx-auto !my-auto ${activeSocialNetwork == "tiktok" ? `!h-full !w-auto` : `!w-full`}`}`}>
                    <div className="w-full rounded-2xl bg-secondary-500 dark:bg-gray-800 flex flex-col">
                        {!isSimpleView && (
                            <>
                                <div className='bg-secondary-500 dark:bg-gray-800 w-full rounded-t-2xl border-b border-tertiary-100 dark:border-gray-800/50 flex flex-col divide-y divide-tertiary-200 dark:divide-gray-700'>
                                    <div className='p-3 relative flex items-center justify-between gap-4 lg:[.fullScreen_&]:!gap-6'>
                                        <PostTagsButton 
                                            onClickTag={(tag) => onClickTag(tag)}
                                            selectedTags={postGroup?.tags}
                                        />
                                        <div className='flex items-center gap-2'>
                                            {showPostArticleButton && <PostArticleButton 
                                                postGroup={postGroup} 
                                                onSave={onSave}  
                                                onPostNow={onPostNow}
                                                status={status}
                                                updateFieldValuePostGroup={updateFieldValuePostGroup}
                                                isLoading={isLoadingPostArticleButton}
                                                showSaveButton={showSaveButton}
                                                size={btnSize}
                                            />}
                                            {showOptionsButton && <PostArticleOptionsButton 
                                                status={status}
                                                onDeletePostGroup={onDeletePostGroup}
                                                onToggleFullScreen={onToggleFullScreen}
                                                onDuplicatePostGroup={!!duplicate ? onDuplicatePostGroup : undefined}
                                                onExportAsHTML={onExportAsHTML}
                                                postGroup={postGroup}
                                                forceFullScreen={forceFullScreen}
                                                activeSocialNetwork={activeSocialNetwork}
                                                size={btnSize}
                                            />}
                                            {fullScreen && (
                                                <Button 
                                                    onClick={() => onToggleFullScreen()}
                                                    className="!w-full !px-2.5"
                                                    variant="plain"
                                                    size="sm"
                                                    icon={<HiOutlineX />}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <PostArticleSocials 
                                    status={status}
                                    posts={posts}
                                    activeSocialNetwork={activeSocialNetwork}
                                    isEditable={isEditable}
                                    setActiveSocialNetwork={setActiveSocialNetwork}
                                    onCreateNetworkPost={onCreateNetworkPost}
                                    onDeleteNetworkPost={onDeleteNetworkPost}
                                    onReplaceNetworkPost={onReplaceNetworkPost}
                                    validationErrors={validationErrors}
                                    postGroup={postGroup}
                                />
                                <ValidationErrors 
                                    activeSocialNetwork={activeSocialNetwork}
                                    validationErrors={validationErrors}
                                />
                                <PublishErrors 
                                    activeSocialNetwork={activeSocialNetwork}
                                    postGroup={postGroup}
                                />
                            </>
                        )}
                        <div className={`transition-all relative !rounded-b-2xl !pb-2 !z-[0] ${expandablePost && !postExpanded && `max-h-[500px] overflow-y-hidden`}`} style={{ zoom: fullScreen ? previewZoom : previewZoom }}>
                            <PreviewComponent 
                                className="w-full !max-w-none !rounded-b-2xl"
                                imgClassName="!aspect-square"
                                muted={true}
                                isEditable={isEditable && !isSimpleView}
                                postIndex={postIndex}
                                onAssetDelete={onAssetDelete}
                                onAssetEdit={onAssetEdit}
                                onAssetNew={onAssetNew}
                                onTextEdit={onTextEdit}
                                onTitleEdit={onTitleEdit}
                                onLinkEdit={onLinkEdit}
                                onCreateNetworkPost={onCreateNetworkPost}
                                onDeleteNetworkPost={onDeleteNetworkPost}
                                onDeleteIndexPost={onDeleteIndexPost}
                                onPreferencesEdit={onPreferencesEdit}
                                onCoverEdit={onCoverEdit}
                                allPosts={posts}
                                isSimpleView={isSimpleView}
                                onCommentsToCreateEdit={onCommentsToCreateEdit}
                                postGroup={postGroup}
                                {...posts?.filter(e => e?.network == activeSocialNetwork)?.length > 1 ? posts?.filter(e => e?.network == activeSocialNetwork) : posts?.filter(e => e?.network == activeSocialNetwork)?.[0]} 
                            />
                            {expandablePost && 
                                <div className={`text-tertiary-700 dark:text-white font-semibold flex items-end justify-center ${!postExpanded ? `!rounded-b-2xl absolute w-full h-full left-0 bottom-0 !h-[35%] bg-gradient-to-b from-transparent via-secondary-500/70 to-white dark:via-gray-800/70 dark:to-gray-800` : ``}`}>
                                    <div 
                                        onClick={() => setPostExpanded(!postExpanded)} 
                                        className='p-4 w-full text-center cursor-pointer hover:opacity-80 transition-all flex items-center gap-2 justify-center'
                                    >
                                        {!postExpanded 
                                            ? <>
                                                Expand Post 
                                                <HiOutlineChevronDown />
                                            </>
                                            : <>
                                                Compress Post 
                                                <HiOutlineChevronUp />
                                            </>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default PostArticle;